/** Inline print rules — one preview sheet (595×842px) = one physical page, no extra margins. */
import {
    ATS_CV_RAIL_PAD_LEFT,
    ATS_CV_MARGIN_BOTTOM,
    ATS_CV_MARGIN_TOP,
    ATS_CV_MARGIN_X,
    ATS_CV_PAGE_MIN_HEIGHT,
    ATS_CV_PAGE_WIDTH,
} from "./atsCvLayout.js";

const PRINT_STYLE = `
  @page {
    size: ${ATS_CV_PAGE_WIDTH}px ${ATS_CV_PAGE_MIN_HEIGHT}px;
    margin: 0;
  }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
  }
  .ats-cv-pages-stack {
    display: block !important;
    gap: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  /* Each sheet sits inside a slide wrapper that carries screen-only padding and
     can be hidden in single-page preview mode. Flatten whatever the wrapper is,
     and put the page breaks here — this, not the sheet, is what the stack
     paginates. Declared before the sheet rule so the sheet keeps its padding. */
  .ats-cv-pages-stack > * {
    display: block !important;
    width: auto !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    page-break-after: always;
    break-after: page;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .ats-cv-pages-stack > *:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  .ats-cv-page-sheet {
    width: ${ATS_CV_PAGE_WIDTH}px !important;
    height: ${ATS_CV_PAGE_MIN_HEIGHT}px !important;
    min-height: ${ATS_CV_PAGE_MIN_HEIGHT}px !important;
    max-height: ${ATS_CV_PAGE_MIN_HEIGHT}px !important;
    margin: 0 !important;
    padding: ${ATS_CV_MARGIN_TOP}px ${ATS_CV_MARGIN_X}px ${ATS_CV_MARGIN_BOTTOM}px !important;
    overflow: hidden !important;
    box-shadow: none !important;
    border: none !important;
    box-sizing: border-box !important;
    page-break-after: auto;
    break-after: auto;
  }
  .ats-cv-page-sheet.tpl-sidebar,
  .ats-cv-page-sheet.tpl-split {
    padding-left: ${ATS_CV_RAIL_PAD_LEFT.sidebar}px !important;
  }
  .ats-cv-page-sheet.tpl-bold {
    padding-left: ${ATS_CV_RAIL_PAD_LEFT.bold}px !important;
  }
  /* Fills (the sidebar rail, section bars, rules) are part of the design. */
  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .ats-cv-page-sheet__badge {
    display: none !important;
  }
`;

/** A media query that targets print (`print`, `print and (...)`) but not screen. */
function isPrintOnly(mediaText) {
    return /(^|,)\s*(only\s+)?print\b/i.test(mediaText) && !/\bscreen\b/i.test(mediaText);
}

/**
 * Serialise a stylesheet, dropping its print-only blocks.
 * @returns {string|null} null when the sheet cannot be read (cross-origin)
 */
function cssTextWithoutPrintRules(sheet) {
    let rules;
    try {
        rules = sheet.cssRules;
    } catch {
        return null;
    }

    return Array.from(rules)
        .filter((rule) => !(rule.type === CSSRule.MEDIA_RULE && isPrintOnly(rule.media.mediaText)))
        .map((rule) => rule.cssText)
        .join("\n");
}

/**
 * CSS text of a <style> element with print-only blocks removed. Falls back to
 * the raw text if the sheet is not readable for any reason.
 */
function screenOnlyCss(styleNode) {
    if (!styleNode.sheet) return styleNode.textContent;
    const cleaned = cssTextWithoutPrintRules(styleNode.sheet);
    // An empty result is legitimate (a sheet that was nothing but print rules),
    // so only an unreadable sheet falls back to the raw text.
    return cleaned === null ? styleNode.textContent : cleaned;
}

const escapeStyleText = (css) => css.replace(/<\/style>/gi, "<\\/style>");

/**
 * One <link>/<style> of the host page rewritten for the print iframe, with the
 * app's own `@media print` blocks dropped.
 *
 * A production build ships its CSS as a linked bundle, and that bundle carries
 * the app-wide print rules — including `body > *:not(#print-clone) { display:
 * none }`, which hides the cloned stack and prints a blank sheet. So a link is
 * inlined as stripped CSS whenever its sheet can be read (same origin), and
 * copied as a link only when it cannot be (a cross-origin CDN / font service),
 * where there is nothing app-specific to strip anyway.
 */
function printableStyleTag(node) {
    if (node.tagName !== "LINK") {
        return `<style>${escapeStyleText(screenOnlyCss(node))}</style>`;
    }
    const cleaned = node.sheet ? cssTextWithoutPrintRules(node.sheet) : null;
    return cleaned === null
        ? `<link rel="stylesheet" href="${node.href}" />`
        : `<style>${escapeStyleText(cleaned)}</style>`;
}

/**
 * Print CV pages in an isolated iframe so Mantine layout / browser reflow
 * cannot add margins or extra pages. Clones the live preview page stack.
 * @param {HTMLElement | null | undefined} previewRoot `.ats-cv-pages-root`
 */
export function printAtsCvPreview(previewRoot) {
    const stack = previewRoot?.querySelector(".ats-cv-pages-stack");
    if (!stack) {
        window.print();
        return;
    }

    const clone = stack.cloneNode(true);
    clone.querySelectorAll(".ats-cv-page-sheet__badge").forEach((el) => el.remove());

    // Copy *every* style source in document order — bundled CSS arrives as inline
    // <style> tags (dev builds, and anything injected at runtime), so links alone
    // leave the iframe unstyled and the print looks nothing like the preview.
    //
    // The app's own `@media print` blocks are dropped on the way in: they are
    // written for the main document (one of them hides every body child that is
    // not the print-modal clone, which would blank this page), and the iframe
    // gets its print behaviour from PRINT_STYLE below instead.
    const styleMarkup = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
        .map(printableStyleTag)
        .join("\n");

    // Same-origin sheets attached via adoptedStyleSheets have no DOM node to copy.
    const adoptedCss = Array.from(document.adoptedStyleSheets || [])
        .map(cssTextWithoutPrintRules)
        .filter(Boolean)
        .join("\n");

    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "CV print");
    iframe.setAttribute("aria-hidden", "true");
    Object.assign(iframe.style, {
        position: "fixed",
        right: "0",
        bottom: "0",
        width: "0",
        height: "0",
        border: "0",
        visibility: "hidden",
    });
    document.body.appendChild(iframe);

    const win = iframe.contentWindow;
    const doc = win.document;

    doc.open();
    doc.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>CV Print</title>
${styleMarkup}
${adoptedCss ? `<style>${adoptedCss}</style>` : ""}
<style>${PRINT_STYLE}</style>
</head>
<body>${clone.outerHTML}</body>
</html>`);
    doc.close();

    let cleaned = false;
    const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        iframe.remove();
    };

    const triggerPrint = () => {
        try {
            win.focus();
            win.print();
        } catch {
            /* ignore */
        }
    };

    win.addEventListener("afterprint", cleanup, { once: true });
    setTimeout(cleanup, 120000);

    const links = doc.querySelectorAll('link[rel="stylesheet"]');
    if (!links.length) {
        setTimeout(triggerPrint, 150);
        return;
    }

    let pending = links.length;
    let fired = false;
    const finish = () => {
        pending -= 1;
        if (pending <= 0 && !fired) {
            fired = true;
            setTimeout(triggerPrint, 100);
        }
    };

    links.forEach((link) => {
        if (link.sheet) {
            finish();
        } else {
            link.addEventListener("load", finish);
            link.addEventListener("error", finish);
        }
    });

    setTimeout(() => {
        if (!fired) {
            fired = true;
            triggerPrint();
        }
    }, 2500);
}
